<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import { RegisterLevel, registerLevelTitle } from '@/enums/quality/register-level'
import RiskActionTiles from '@/views/quality/risk/dashboard/RiskActionTiles.vue'
import RiskAttentionQueue from '@/views/quality/risk/dashboard/RiskAttentionQueue.vue'
import RiskByCategoryChart from '@/views/quality/risk/dashboard/RiskByCategoryChart.vue'
import RiskGovernanceCard from '@/views/quality/risk/dashboard/RiskGovernanceCard.vue'
import RiskComplianceCard from '@/views/quality/risk/dashboard/RiskComplianceCard.vue'
import RiskHeatMap from '@/views/quality/risk/dashboard/RiskHeatMap.vue'
import RiskMovementCard from '@/views/quality/risk/dashboard/RiskMovementCard.vue'

// unplugin-vue-router derives the route name from the file path
// (`app-quality-risk-dashboard`), confirmed against typed-router.d.ts — see
// src/pages/app/quality/risk/governance.vue for the same convention.
definePage({
  meta: {
    subject: 'risk.menu',
    action: 'dashboard',
  },
})

// Reactive, not a one-time snapshot: the navbar unit switcher
// (DefaultLayoutWithVerticalNav.vue) writes this same cookie key live, and a
// dashboard that kept showing a previously-selected unit's numbers after the
// user switched units would itself be the cross-unit leak this module
// already shipped once.
const selectedUnitId = cookieRef('selectedUnit')

// Department filter options. dashboard/risk/tiles has no way to express
// "department_id IS NULL": RiskAnalyticsService::baseQuery() only applies the
// filter `if (! empty($filters['department_id']))`, and createUrl() strips
// null/undefined/'' query params before the request is even sent — so a
// zero, empty-string or omitted department_id are all indistinguishable from
// "no filter" by the time they reach the controller. An explicit
// "No department" option that claims to isolate unassigned risks would
// silently return the whole unit instead, which is exactly the kind of
// dashboard lie this task is warned against — so it is not offered. See
// task-5-report.md for detail.
const { data: departmentsData } = useApi(createUrl('/masters/departments', {
  query: { showAll: true, select: ['id', 'name'] },
}))

const departments = computed(() => departmentsData.value?.data ?? departmentsData.value ?? [])

const departmentId = ref(null)
const registerLevel = ref(null)

const registerLevelOptions = [
  { title: 'All levels', value: null },
  ...Object.values(RegisterLevel).map(value => ({ title: registerLevelTitle(value), value })),
]

// Owned here (not inside RiskActionTiles) because the queue widget below
// binds to this ref directly too. Defaults to the first queue rather than
// null so the queue widget (and the tile it mirrors) always has a selection
// to show on first load, before the user has clicked any tile.
const selectedQueue = ref('unassessed')

// Totals/active/closed come back on the same tiles response the action
// tiles fetch, but they are not tile candidates themselves (nobody acts on
// "23 total risks"), so RiskActionTiles hands them up via `update:summary`
// rather than the page issuing a second, duplicate request for the same
// endpoint.
const summary = ref(null)

// `active` is NOT IN (ACCEPTED, CLOSED) — see Risk::scopeActive and
// RiskStatus::terminalKeys — so total/active/closed alone never add up and the
// reader is left hunting for the remainder (21 · 16 · 1 left 4 unexplained).
// Those are the accepted risks; the three statuses partition the register, so
// the count is exact rather than a guess.
const acceptedCount = computed(() => {
  if (!summary.value) return 0

  return Math.max(0, summary.value.total - summary.value.active - summary.value.closed)
})

const subtitle = computed(() => {
  if (!summary.value) return 'Snapshot of the current risk register'

  const parts = [
    `${summary.value.total} total risks`,
    `${summary.value.active} active`,
  ]

  if (acceptedCount.value) parts.push(`${acceptedCount.value} accepted`)
  parts.push(`${summary.value.closed} closed`)

  return parts.join(' · ')
})
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-6">
      <div>
        <div class="text-h5">
          Risk Dashboard
        </div>
        <div class="text-body-2 text-medium-emphasis">
          {{ subtitle }}
        </div>
      </div>

      <div
        v-if="selectedUnitId"
        class="d-flex flex-wrap gap-3"
      >
        <div style="inline-size: 14rem;">
          <AppAutocomplete
            v-model="departmentId"
            :items="departments"
            item-title="name"
            item-value="id"
            placeholder="All departments"
            density="compact"
            clearable
          />
        </div>
        <div style="inline-size: 12rem;">
          <AppSelect
            v-model="registerLevel"
            :items="registerLevelOptions"
            item-title="title"
            item-value="value"
            density="compact"
          />
        </div>
      </div>
    </div>

    <!--
      A cross-unit aggregate is meaningless here (the endpoint requires
      unit_id and 403s on a unit the caller doesn't belong to), so nothing below
      fires a request until one is selected. 
    -->
    <VCard
      v-if="!selectedUnitId"
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-column align-center justify-center text-center py-16">
        <VAvatar
          color="secondary"
          variant="tonal"
          size="56"
          rounded
          class="mb-4"
        >
          <VIcon
            icon="tabler-building"
            size="28"
          />
        </VAvatar>
        <div class="text-h6 font-weight-medium mb-1">
          Select a unit
        </div>
        <div class="text-body-2 text-medium-emphasis">
          Choose a unit from the navigation bar above to view its risk dashboard.
        </div>
      </VCardText>
    </VCard>

    <template v-else>
      <VRow>
        <VCol cols="12">
          <RiskActionTiles
            v-model:selected-queue="selectedQueue"
            :unit-id="selectedUnitId"
            :department-id="departmentId"
            :register-level="registerLevel"
            @update:summary="summary = $event"
          />
        </VCol>

        <VCol
          cols="12"
          lg="7"
        >
          <RiskAttentionQueue
            v-model:selected-queue="selectedQueue"
            :unit-id="selectedUnitId"
            :department-id="departmentId"
            :register-level="registerLevel"
            :active-count="summary?.active ?? null"
          />
        </VCol>

        <VCol
          cols="12"
          lg="5"
        >
          <RiskHeatMap
            :unit-id="selectedUnitId"
            :department-id="departmentId"
            :register-level="registerLevel"
          />
        </VCol>

        <VCol cols="12">
          <RiskMovementCard
            :unit-id="selectedUnitId"
            :department-id="departmentId"
            :register-level="registerLevel"
            @update:selected-queue="selectedQueue = $event"
          />
        </VCol>

        <VCol
          cols="12"
          lg="4"
        >
          <RiskGovernanceCard
            :unit-id="selectedUnitId"
            :department-id="departmentId"
            :register-level="registerLevel"
          />
        </VCol>

        <!--
          Deliberately takes only unit_id: assessment compliance is a
          per-unit exercise record, so department and register-level
          filters do not apply to it. 
        -->
        <VCol
          cols="12"
          lg="4"
        >
          <RiskComplianceCard :unit-id="selectedUnitId" />
        </VCol>

        <VCol
          cols="12"
          lg="8"
        >
          <RiskByCategoryChart
            :unit-id="selectedUnitId"
            :department-id="departmentId"
            :register-level="registerLevel"
          />
        </VCol>
      </VRow>
    </template>
  </div>
</template>
